import React, { useState, useEffect } from 'react';
import { soundEffects } from '../../utils/soundEffects';
import { fireDonkatsuConfetti, fireConfettiSides } from '../../hooks/useConfetti';

interface DrawAnimationProps {
  isOpen: boolean;
  winners: Array<{
    userId: string;
    user: {
      id: string;
      name: string;
      email: string;
    };
    isGuaranteed?: boolean;
  }>;
  currentUserId?: string;
  onClose: () => void;
}

const DrawAnimation: React.FC<DrawAnimationProps> = ({
  isOpen,
  winners,
  currentUserId,
  onClose,
}) => {
  const [step, setStep] = useState<'countdown' | 'reveal' | 'celebrate'>('countdown');
  const [countdown, setCountdown] = useState(3);
  const [revealedIndex, setRevealedIndex] = useState(0);
  const [showDonkatsu, setShowDonkatsu] = useState(false);

  const isWinner = winners.some(w => w.userId === currentUserId);
  const myWinner = winners.find(w => w.userId === currentUserId);
  const isGuaranteed = myWinner?.isGuaranteed;

  useEffect(() => {
    if (!isOpen) {
      // 리셋
      setStep('countdown');
      setCountdown(3);
      setRevealedIndex(0);
      setShowDonkatsu(false);
      return;
    }

    // 카운트다운 (3초)
    if (step === 'countdown' && countdown > 0) {
      // 틱톡 소리
      soundEffects.playCountdown();
      
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }

    // 카운트다운 끝 → 발표 시작
    if (step === 'countdown' && countdown === 0) {
      setStep('reveal');
    }

    // 순차 발표 (한 명씩 0.8초 간격)
    if (step === 'reveal' && revealedIndex < winners.length) {
      // 두둥 소리
      soundEffects.playReveal();
      
      const timer = setTimeout(() => {
        setRevealedIndex(revealedIndex + 1);
      }, 800);
      return () => clearTimeout(timer);
    }

    // 발표 끝 → 축하 단계
    if (step === 'reveal' && revealedIndex === winners.length) {
      // 마지막 발표 음향이 끝날 때까지 대기 (0.5초)
      setTimeout(() => {
        setStep('celebrate');
      }, 500);
    }
  }, [isOpen, step, countdown, revealedIndex, winners.length, isWinner]);

  // 축하 단계 음향 (별도 useEffect)
  useEffect(() => {
    if (step !== 'celebrate') return;
    
    console.log('🎉 Celebrate 단계 시작! isWinner:', isWinner);
    
    // 본인 당첨 시 돈까스 애니메이션 + 음향 + Confetti
    if (isWinner) {
      console.log('🎊 당첨자 음향 재생 시작!');
      
      // 즉시: 짜잔 멜로디 + 양쪽 폭죽
      soundEffects.playWin();
      fireConfettiSides();
      setShowDonkatsu(true);
      
      // 0.5초 후: 바삭 소리 + 돈까스 폭죽
      const donkatsuTimer = setTimeout(() => {
        console.log('🍖 바삭 소리!');
        soundEffects.playDonkatsu();
        fireDonkatsuConfetti();
      }, 500);
      
      // 1.0초 후: 함성 박수
      const applauseTimer = setTimeout(() => {
        console.log('👏 함성 박수!');
        soundEffects.playApplause();
      }, 1000);
      
      // 3초 후: 애니메이션 종료
      const endTimer = setTimeout(() => {
        console.log('✅ 애니메이션 종료');
        setShowDonkatsu(false);
      }, 3000);
      
      // 클린업
      return () => {
        clearTimeout(donkatsuTimer);
        clearTimeout(applauseTimer);
        clearTimeout(endTimer);
      };
    }
  }, [step, isWinner]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl">
        {/* 카운트다운 */}
        {step === 'countdown' && (
          <div className="text-center">
            <h2 className="text-2xl font-bold text-primary mb-8">
              🎲 추첨 시작!
            </h2>
            <div className="text-9xl font-bold text-accent animate-bounce">
              {countdown}
            </div>
          </div>
        )}

        {/* 순차 발표 */}
        {step === 'reveal' && (
          <div className="space-y-4">
            <h2 className="text-xl font-bold text-primary text-center mb-6">
              🎉 당첨자 발표
            </h2>
            <div className="space-y-3 max-h-80 overflow-y-auto">
              {winners.slice(0, revealedIndex).map((winner, index) => {
                const isMe = winner.userId === currentUserId;
                return (
                  <div
                    key={winner.userId}
                    className={`p-4 rounded-xl border-2 transform transition-all duration-500 ${
                      isMe
                        ? 'bg-gradient-to-r from-orange-400 to-orange-500 text-white border-orange-600 scale-105'
                        : 'bg-gradient-to-r from-green-50 to-emerald-50 text-gray-800 border-green-200'
                    } animate-fadeIn`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-bold">
                          {isMe ? '🎊 ' : '✨ '}
                          {winner.user.name}
                          {isMe ? ' (나)' : ''}
                        </div>
                        {winner.isGuaranteed && (
                          <div className="text-xs mt-1 opacity-90">
                            🎯 보장 당첨
                          </div>
                        )}
                      </div>
                      <div className="text-3xl">
                        {index + 1}등
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* 축하 단계 */}
        {step === 'celebrate' && (
          <div className="text-center space-y-6">
            {/* 디버깅: step 표시 */}
            <div className="text-xs text-gray-400 mb-2">
              Step: {step} | Winner: {isWinner ? 'YES' : 'NO'}
            </div>
            
            {isWinner ? (
              <>
                <div className="text-6xl mb-4 animate-bounce">🎉</div>
                <h2 className="text-3xl font-bold text-accent mb-4">
                  {isGuaranteed ? '🎯 보장 당첨!' : '축하합니다!'}
                </h2>
                <p className="text-xl text-gray-700 mb-6">
                  당첨되셨습니다! 🎊
                </p>
                
                {/* 돈까스 바삭 당첨 애니메이션 */}
                {showDonkatsu && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center justify-center animate-ping">
                      <div className="text-9xl">✨</div>
                    </div>
                    <div className="relative text-9xl animate-bounce">
                      🍖
                    </div>
                    <div className="mt-4 text-2xl font-bold text-accent animate-pulse">
                      바삭 당첨! 🔥
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="text-6xl mb-4">📋</div>
                <h2 className="text-2xl font-bold text-gray-700 mb-4">
                  추첨 완료!
                </h2>
                <p className="text-lg text-gray-600">
                  총 {winners.length}명이 당첨되었습니다
                </p>
              </>
            )}

            <button
              onClick={onClose}
              className="btn-azocsii w-full py-3 text-lg mt-6"
            >
              확인
            </button>
            
            {/* 디버깅: 음향 테스트 버튼 */}
            {isWinner && (
              <div className="text-xs text-gray-400 space-x-2 mt-2">
                <button onClick={() => soundEffects.playWin()} className="underline">짜잔</button>
                <button onClick={() => soundEffects.playDonkatsu()} className="underline">바삭</button>
                <button onClick={() => soundEffects.playApplause()} className="underline">박수</button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DrawAnimation;
